import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { collapsedTextIndex } from './page-guide';

/** An open comment's place in the text: the quote and which of its appearances was meant. */
export interface CommentHighlight {
  id: string;
  quote: string;
  occurrence: number;
}

interface CommentHighlightState {
  highlights: CommentHighlight[];
  activeId: string | null;
  decorations: DecorationSet;
}

const highlightKey = new PluginKey<CommentHighlightState>('commentHighlight');
const highlightUpdateKey = new PluginKey('commentHighlightUpdate');

/** Where the nth appearance of a quote sits in the document, as positions, or null. */
export const locateQuote = (
  doc: ProseMirrorNode,
  quote: string,
  occurrence: number
): { from: number; to: number } | null => {
  return locateQuoteIn(collapsedTextIndex(doc), quote, occurrence);
};

const locateQuoteIn = (
  { text, positions }: ReturnType<typeof collapsedTextIndex>,
  quote: string,
  occurrence: number
): { from: number; to: number } | null => {
  const needle = quote.replace(/\s+/g, ' ').trim();
  if (!needle) return null;
  let at = -1;
  for (let n = 0; n <= occurrence; n += 1) {
    at = text.indexOf(needle, at + 1);
    if (at < 0) return null;
  }
  const last = at + needle.length - 1;
  return { from: positions[at], to: positions[last] + 1 };
};

const buildDecorations = (
  doc: ProseMirrorNode,
  highlights: CommentHighlight[],
  activeId: string | null
) => {
  const index = collapsedTextIndex(doc);
  return DecorationSet.create(
    doc,
    highlights.flatMap((h) => {
      const range = locateQuoteIn(index, h.quote, h.occurrence);
      if (!range) return [];
      return [
        Decoration.inline(range.from, range.to, {
          class: h.id === activeId ? 'cara-comment-mark is-active' : 'cara-comment-mark',
          'data-comment-id': h.id
        })
      ];
    })
  );
};

export const setCommentHighlights = (
  editor: Editor,
  highlights: CommentHighlight[],
  activeId: string | null
) => {
  if (!editor.view || editor.isDestroyed) return;
  editor.view.dispatch(editor.state.tr.setMeta(highlightUpdateKey, { highlights, activeId }));
};

/** Selects the comment's text and brings it into view; false when the text is gone. */
export const revealComment = (editor: Editor, highlight: CommentHighlight): boolean => {
  if (!editor.view || editor.isDestroyed) return false;
  const range = locateQuote(editor.state.doc, highlight.quote, highlight.occurrence);
  if (!range) return false;
  const tr = editor.state.tr
    .setSelection(TextSelection.create(editor.state.doc, range.from, range.to))
    .scrollIntoView();
  editor.view.dispatch(tr);
  return true;
};

export const CommentHighlightExtension = Extension.create({
  name: 'commentHighlight',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: highlightKey,
        state: {
          init: () =>
            ({
              highlights: [],
              activeId: null,
              decorations: DecorationSet.empty
            }) as CommentHighlightState,
          apply(tr, value): CommentHighlightState {
            const next = tr.getMeta(highlightUpdateKey) as
              | { highlights: CommentHighlight[]; activeId: string | null }
              | undefined;
            if (next) {
              return {
                ...next,
                decorations: buildDecorations(tr.doc, next.highlights, next.activeId)
              };
            }
            // An edit moves the text; the quote is searched for again rather than mapped, so
            // a highlight follows its words and vanishes with them, as the anchor does.
            if (tr.docChanged && value.highlights.length > 0) {
              return {
                ...value,
                decorations: buildDecorations(tr.doc, value.highlights, value.activeId)
              };
            }
            return value;
          }
        },
        props: {
          decorations(state) {
            return highlightKey.getState(state)?.decorations;
          }
        }
      })
    ];
  }
});
